import type { SupportedCountryCode } from '@seek/ca-myprofile-utils';
import { type RenderParams, createForm } from '@seek/forms-ui';
import { FormActionButtons } from '@seek/profile-edit-ui';
import { useTranslations } from '@vocab/react';
import { Stack } from 'braid-design-system';
import { useState } from 'react';

import type { DirtyFormProps } from 'src/components/FormDrawer/FormDrawer';
import { useOnFormDirtyChange } from 'src/components/FormDrawer/useOnFormDirtyChange';
import { SALARY } from 'src/components/Router';
import { useEnterReadMode } from 'src/components/Router/useEnterReadMode';
import { useAppConfig } from 'src/modules/appConfig';
import { useFocus } from 'src/modules/hooks/useFocus';
import type { ActionName } from 'src/types';
import type {
  Maybe,
  SalaryPreference2,
  SalaryPreference2Input,
  SalaryType2,
} from 'src/types/graphql';

import { useSalaryPreferences, useUpdateSalaryPreferences } from '../data';
import {
  type SalaryPreferenceTrackingData,
  useUpdateSalaryPreferenceAnalytics,
} from '../useUpdateSalaryPreferenceAnalytics';

import translations from './.vocab';
import { SalaryItem } from './SalaryItem/SalaryItem';

export interface SalaryFormValues {
  // possible salary type fields for supported countries
  salaryType_AU?: SalaryType2;
  salaryType_NZ?: SalaryType2;
  salaryType_GB?: SalaryType2;
  salaryType_ID?: SalaryType2;
  salaryType_HK?: SalaryType2;
  salaryType_TH?: SalaryType2;
  salaryType_MY?: SalaryType2;
  salaryType_SG?: SalaryType2;
  salaryType_PH?: SalaryType2;
  // possible amount fields for supported countries
  amount_AU?: Maybe<number>;
  amount_NZ?: Maybe<number>;
  amount_GB?: Maybe<number>;
  amount_ID?: Maybe<number>;
  amount_HK?: Maybe<number>;
  amount_TH?: Maybe<number>;
  amount_MY?: Maybe<number>;
  amount_SG?: Maybe<number>;
  amount_PH?: Maybe<number>;
}

type SalaryFormItemValues = Pick<
  SalaryFormValues,
  `amount_${SupportedCountryCode}` | `salaryType_${SupportedCountryCode}`
>;

type FormProps = RenderParams<SalaryFormValues> & MappedProps;

type MappedProps = ReturnType<typeof mapFormStateToProps>;

export const { FormProvider, useField: useSalaryFormField } = createForm<
  SalaryFormValues,
  MappedProps
>('salary-form', {
  formType: 'braid',
});

const mapFormStateToProps = ({
  dirty: isFieldDirty,
  form: { dirty: isFormDirty },
}: {
  dirty: Record<keyof SalaryFormValues, boolean>;
  form: {
    dirty: boolean;
  };
}) => ({
  isFieldDirty,
  isFormDirty,
});

export const EditSalaryForm = ({
  isFormDirty,
  isFieldDirty,
  handleSubmit,
  onFormDirtyChange,
  setFieldValue,
}: FormProps & DirtyFormProps) => {
  const { t } = useTranslations(translations);

  const { salaryPreferences, loading, error } = useSalaryPreferences();
  const { locale, language: languageCode } = useAppConfig();
  const [updateSalaryPreference] = useUpdateSalaryPreferences();
  const { updateSalaryPreferenceAnalytics } =
    useUpdateSalaryPreferenceAnalytics();

  const [salaryEvents, setSalaryEvents] = useState<
    SalaryPreferenceTrackingData[]
  >([]);

  const enterReadMode = useEnterReadMode();

  useOnFormDirtyChange({ onFormDirtyChange, dirty: isFormDirty });

  const salaryRef = useFocus<HTMLInputElement>({
    card: SALARY,
    fieldName: 'salary',
    domReady: !loading && !error,
  });

  const addSalaryEvent = ({
    actionName,
    salaryCurrency,
  }: {
    actionName: ActionName;
    salaryCurrency: SalaryPreference2['currencyCode'];
  }) => {
    setSalaryEvents((oldList) => [
      ...oldList,
      {
        actionName,
        salaryCurrency,
      },
    ]);
  };

  const handleOnSubmit = (values: SalaryFormValues) => {
    if (isFormDirty) {
      const inputList: SalaryPreference2Input[] = salaryPreferences.map(
        ({ id, country: { countryCode } }) => ({
          id,
          countryCode,
          amount: values[`amount_${countryCode as SupportedCountryCode}`],
          salaryType: values[
            `salaryType_${countryCode as SupportedCountryCode}`
          ] as SalaryType2,
        }),
      );

      updateSalaryPreference({
        input: {
          salaryPreferences: inputList,
        },
        countryCode: locale,
        languageCode,
      });
      updateSalaryPreferenceAnalytics(salaryEvents);
    }
    enterReadMode();
  };

  return (
    <form onSubmit={handleSubmit(handleOnSubmit)}>
      <Stack space="large">
        <Stack space="medium">
          {salaryPreferences.map((salaryItem, index) => (
            <SalaryItem
              label={salaryItem.country.name}
              key={`salary-${salaryItem.country.countryCode}`}
              ref={index === 0 ? salaryRef : undefined}
              countryCode={
                salaryItem.country.countryCode as SupportedCountryCode
              }
              currencyCode={salaryItem.currencyCode}
              addSalaryEvent={addSalaryEvent}
              setFieldValue={setFieldValue}
              isFieldDirty={isFieldDirty}
            />
          ))}
        </Stack>
        <FormActionButtons
          cardName={t('Salary expectation')}
          dataAutomationSuffix="salary"
          onCancel={enterReadMode}
        />
      </Stack>
    </form>
  );
};

export const EditSalary = (dirtyFormProps: DirtyFormProps) => {
  const { salaryPreferences } = useSalaryPreferences();
  const { language } = useAppConfig();

  const salaryItemsInitialValues = salaryPreferences.reduce(
    (salaryItems, { country: { countryCode }, amount, salaryType }) => {
      salaryItems[`amount_${countryCode as SupportedCountryCode}`] = amount;
      salaryItems[`salaryType_${countryCode as SupportedCountryCode}`] =
        salaryType;
      return salaryItems;
    },
    {} as SalaryFormItemValues,
  );

  return (
    <FormProvider
      mapFormStateToProps={mapFormStateToProps}
      initialValues={{ ...salaryItemsInitialValues }}
      language={language}
    >
      {(formProps) => <EditSalaryForm {...dirtyFormProps} {...formProps} />}
    </FormProvider>
  );
};
